import type {
  BidSummary,
  PublishedTaskDetail,
  PublishedTaskListItem,
  PublishedTaskQuery,
  PublishedTaskStats,
} from '@/types/userCenter/publishedTask'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { baseDataApi, type TaskTypeItem } from '@/api/baseData'
import { publishedTasksApi } from '@/api/userCenter/publishedTasks'

const DEFAULT_STATS: PublishedTaskStats = {
  totalTasks: 0,
  inProgress: 0,
  completed: 0,
  cancelled: 0,
  totalBudget: 0,
  avgCompletionDays: 0,
}

export const usePublishedTaskStore = defineStore('user-published-task-v2', () => {
  const loading = ref(false)
  const tasks = ref<PublishedTaskListItem[]>([])
  const stats = ref<PublishedTaskStats>({ ...DEFAULT_STATS })
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(10)
  const queryParams = ref<PublishedTaskQuery>({ page: 1, pageSize: 10 })
  const currentTask = ref<PublishedTaskDetail | null>(null)
  const currentBids = ref<BidSummary[]>([])
  const categories = ref<{ id: string, name: string }[]>([])

  const fetchCategories = async () => {
    try {
      const res = await baseDataApi.getTaskTypes()
      if (res && res.data) {
        categories.value = res.data.map((item: TaskTypeItem) => ({
          id: item.code,
          name: item.name,
        }))
      }
      else if (Array.isArray(res)) {
        categories.value = res.map((item: TaskTypeItem) => ({
          id: item.code,
          name: item.name,
        }))
      }
    }
    catch (error) {
      console.error('[PublishedTaskStore] fetchCategories error:', error)
    }
  }

  const setPage = (value: number) => {
    page.value = value
    queryParams.value.page = value
  }

  const setPageSize = (value: number) => {
    pageSize.value = value
    queryParams.value.pageSize = value
  }

  const setQuery = (payload: Partial<PublishedTaskQuery>) => {
    queryParams.value = {
      ...queryParams.value,
      ...payload,
    }
  }

  const fetchPublishedTasks = async (params?: Partial<PublishedTaskQuery>) => {
    loading.value = true
    try {
      const merged: PublishedTaskQuery = {
        ...queryParams.value,
        ...params,
      }
      merged.page = merged.page ?? page.value
      merged.pageSize = merged.pageSize ?? pageSize.value

      const data = await publishedTasksApi.getPublishedTasks(merged)
      stats.value = data?.stats ?? { ...DEFAULT_STATS }
      tasks.value = data?.records ?? data?.list ?? []
      total.value = data?.pagination?.total ?? data?.total ?? 0
      page.value = data?.page ?? merged.page ?? 1
      pageSize.value = data?.pageSize ?? merged.pageSize ?? 10
      queryParams.value = {
        ...merged,
        page: page.value,
        pageSize: pageSize.value,
      }
    }
    catch (error: unknown) {
      console.error('[PublishedTaskStore] fetchPublishedTasks error:', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  const fetchTaskDetail = async (taskId: string) => {
    loading.value = true
    try {
      const data = await publishedTasksApi.getTaskDetail(taskId)
      currentTask.value = data
      tasks.value = tasks.value.map(item => (item.id === taskId ? { ...item, ...data } : item))
      return data
    }
    catch (error: unknown) {
      console.error('[PublishedTaskStore] fetchTaskDetail error:', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  const fetchTaskBids = async (taskId: string) => {
    loading.value = true
    try {
      const data = await publishedTasksApi.getTaskBids(taskId)
      currentBids.value = data ?? []
      tasks.value = tasks.value.map(item => (item.id === taskId ? { ...item, bids: data ?? [] } : item))
      return data
    }
    catch (error: unknown) {
      console.error('[PublishedTaskStore] fetchTaskBids error:', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  const selectBid = async (taskId: string, bidId: string) => {
    loading.value = true
    try {
      await publishedTasksApi.selectBid(taskId, bidId)
      const bids = await fetchTaskBids(taskId)
      await fetchTaskDetail(taskId)
      if (bids) {
        const updated: BidSummary[] = (bids ?? []).map(bid => ({
          ...bid,
          status: bid.id === bidId ? 'selected' : 'rejected',
        }))
        currentBids.value = updated
        tasks.value = tasks.value.map(item =>
          item.id === taskId ? { ...item, selectedBidId: bidId, bids: updated } : item,
        )
        if (currentTask.value && currentTask.value.id === taskId) {
          currentTask.value = {
            ...currentTask.value,
            selectedBidId: bidId,
            bids: updated,
          }
        }
      }
      return { success: true, message: '选标成功' }
    }
    catch (error: unknown) {
      console.error('[PublishedTaskStore] selectBid error:', error)
      const message = error instanceof Error ? error.message : '选标失败'
      return { success: false, message }
    }
    finally {
      loading.value = false
    }
  }

  const terminateTask = async (taskId: string) => {
    loading.value = true
    try {
      await publishedTasksApi.terminateTask(taskId)
      await fetchTaskDetail(taskId)
      // 更新列表状态
      tasks.value = tasks.value.map(item =>
        item.id === taskId ? { ...item, status: 'terminated' as const } : item,
      )
      return { success: true, message: '任务已终止' }
    }
    catch (error: unknown) {
      console.error('[PublishedTaskStore] terminateTask error:', error)
      const message = error instanceof Error ? error.message : '终止任务失败'
      return { success: false, message }
    }
    finally {
      loading.value = false
    }
  }

  return {
    loading,
    tasks,
    stats,
    total,
    page,
    pageSize,
    queryParams,
    categories,
    currentTask,
    currentBids,
    fetchPublishedTasks,
    fetchTaskDetail,
    fetchTaskBids,
    selectBid,
    terminateTask,
    fetchCategories,

    setQuery,
    setPage,
    setPageSize,
  }
})
